import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Modal, Badge, Row, Col, Table, Spinner, Alert } from 'react-bootstrap';
import { getConnectionStatus } from '../services/api';
import { subscribeToTickerUpdates, getSocketStatus, reconnectSocket, initSocket } from '../services/socketService';

const parseTimestamp = (value) => {
  if (!value) {
    return null;
  }

  try {
    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
    const normalized = hasTimezone ? value : `${value}Z`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (error) {
    console.error('StatusButton: Failed to parse timestamp', error);
    return null;
  }
};

const extractLatestTickerTimestamp = (status) => {
  if (!status) {
    return null;
  }

  const candidates = [];
  const lastUpdate = status.last_update || {};

  if (lastUpdate.batch_info?.update_time) {
    candidates.push(lastUpdate.batch_info.update_time);
  }

  if (lastUpdate.time) {
    candidates.push(lastUpdate.time);
  }

  if (Array.isArray(lastUpdate.recent_tickers)) {
    lastUpdate.recent_tickers.forEach((ticker) => {
      if (ticker?.last_updated) {
        candidates.push(ticker.last_updated);
      }
    });
  }

  if (candidates.length === 0) {
    return null;
  }

  let newest = null;
  candidates.forEach((candidate) => {
    const parsed = parseTimestamp(candidate);
    if (parsed && (!newest || parsed > newest)) {
      newest = parsed;
    }
  });

  return newest ? newest.toISOString() : null;
};

const isTimestampNewer = (incoming, known) => {
  const incomingDate = parseTimestamp(incoming);
  if (!incomingDate) {
    return false;
  }

  const knownDate = parseTimestamp(known);
  return !knownDate || incomingDate > knownDate;
};

const StatusButton = ({
  socketStatus,
  lastUpdateTime,
  lastPriceUpdateTime,
  lastPriceUpdateCount,
  isRefreshing,
  showDebugInfo,
  getLastUpdateText,
  getPriceUpdateIndicator,
  handleTestWebSocket,
  isTestingWebSocket,
  toggleDebugMode,
  updateIntervalMinutes = 1
}) => {
  const [showModal, setShowModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showHeartbeat, setShowHeartbeat] = useState(false);
  const [timestampRefreshTick, setTimestampRefreshTick] = useState(0);
  const lastKnownUpdateRef = useRef(null);
  const latestTickerTimestamp = useMemo(
    () => extractLatestTickerTimestamp(connectionStatus),
    [connectionStatus]
  );

  // Fetch connection status when modal opens
  const fetchConnectionStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConnectionStatus();
      setConnectionStatus(response.data);

      const latestTimestamp = extractLatestTickerTimestamp(response.data);
      if (latestTimestamp) {
        lastKnownUpdateRef.current = latestTimestamp;
      }
    } catch (error) {
      console.error('Error fetching connection status:', error);
      setConnectionStatus({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showModal) {
      fetchConnectionStatus();
    }
  }, [showModal, fetchConnectionStatus]);

  useEffect(() => {
    if (latestTickerTimestamp) {
      lastKnownUpdateRef.current = latestTickerTimestamp;
    }
  }, [latestTickerTimestamp]);

  useEffect(() => {
    if (!showModal) {
      return undefined;
    }

    const interval = setInterval(() => {
      setTimestampRefreshTick((tick) => tick + 1);
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, [showModal]);

  // Listen for ticker updates to refresh connection status
  useEffect(() => {
    if (!showModal) {
      return undefined;
    }

    const unsubscribe = subscribeToTickerUpdates((data) => {
      const hasTickerUpdates = Array.isArray(data?.updated_tickers) && data.updated_tickers.length > 0;
      const candidateTimestamp = data?.latest_update_time || data?.time || null;
      const shouldRefresh = hasTickerUpdates || isTimestampNewer(candidateTimestamp, lastKnownUpdateRef.current);

      if (shouldRefresh) {
        console.log('StatusButton: Refreshing connection status due to ticker updates');
        fetchConnectionStatus();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [showModal, fetchConnectionStatus]);


  // Heartbeat effect - triggers pulse animation when lastUpdateTime changes
  useEffect(() => {
    if (lastUpdateTime && socketStatus === 'connected') {
      setShowHeartbeat(true);
      
      // Hide heartbeat animation after 800ms
      const timer = setTimeout(() => {
        setShowHeartbeat(false);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [lastUpdateTime, socketStatus]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedTickers = (tickers) => {
    if (!sortConfig.key) return tickers;
    
    return [...tickers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle symbol sorting (string)
      if (sortConfig.key === 'symbol') {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }
      
      // Handle numeric sorting
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };

  const getStatusVariant = () => {
    switch (socketStatus) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      default:
        return 'danger';
    }
  };

  const getTimestampInfo = (isoString) => {
    if (!isoString) return null;

    try {
      const date = parseTimestamp(isoString);
      if (!date) {
        return null;
      }

      const now = new Date();
      const diffMs = Math.max(0, now.getTime() - date.getTime());
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      let relative;
      if (diffSeconds < 45) {
        relative = 'just now';
      } else if (diffMinutes < 60) {
        relative = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        relative = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else if (diffDays < 7) {
        relative = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      } else {
        const weeks = Math.floor(diffDays / 7);
        relative = `${weeks} week${weeks === 1 ? '' : 's'} ago`;
      }

      const isSameDay = date.toDateString() === now.toDateString();
      const timeFormatter = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit'
      });

      let precise;
      if (isSameDay) {
        precise = timeFormatter.format(date);
      } else {
        const dateFormatter = new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric'
        });
        precise = `${dateFormatter.format(date)} ${timeFormatter.format(date)}`;
      }

      return { relative, precise };
    } catch (error) {
      console.error('StatusButton: Failed to format timestamp', error);
      return null;
    }
  };

  // timestampRefreshTick isn't read by getTimestampInfo, but it's intentionally kept as a
  // dependency: it's a periodic counter (see setInterval above) whose only purpose is to
  // force this memo to recompute the relative-time string ("5 minutes ago") as time passes.
  const recentTickersTimestampInfo = useMemo(
    () => getTimestampInfo(latestTickerTimestamp),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latestTickerTimestamp, timestampRefreshTick]
  );

  const rateLimitsInfo = connectionStatus?.distributed_system?.rate_limits;
  const statusCapacity = rateLimitsInfo?.status?.capacity;
  let derivedCapacity = Number.isFinite(statusCapacity) ? statusCapacity : null;

  if (!Number.isFinite(derivedCapacity)) {
    const safePerMinute = rateLimitsInfo?.status?.safe_limits?.per_minute;
    const rawPerMinute = rateLimitsInfo?.limits?.per_minute;
    if (Number.isFinite(safePerMinute) && Number.isFinite(rawPerMinute) && rawPerMinute > 0) {
      derivedCapacity = safePerMinute / rawPerMinute;
    }
  }

  const capacityPercent = Number.isFinite(derivedCapacity)
    ? Math.round(derivedCapacity * 1000) / 10
    : null;


  return (
    <>
      <style>
        {`
          .status-button-wrapper {
            position: relative;
            display: inline-block;
          }
          
          .heartbeat-ring {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 20px;
            height: 20px;
            border: 2px solid;
            border-color: ${socketStatus === 'connected' ? '#198754' : 
                           socketStatus === 'connecting' ? '#ffc107' : '#dc3545'};
            border-radius: 50%;
            transform: translate(-50%, -50%);
            opacity: 0;
            pointer-events: none;
          }
          
          .heartbeat-ring.animate {
            animation: heartbeat-pulse 0.8s ease-out;
          }
          
          @keyframes heartbeat-pulse {
            0% {
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 0.8;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.8);
              opacity: 0.4;
            }
            100% {
              transform: translate(-50%, -50%) scale(2.5);
              opacity: 0;
            }
          }
        `}
      </style>
      
      <div className="status-button-wrapper">
        <Button
          variant={`outline-${getStatusVariant()}`}
          size="sm"
          onClick={() => setShowModal(true)}
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', minWidth: 'auto', padding: '0.25rem 0.5rem' }}
        >
          <i className={`bi bi-circle-fill`} style={{ 
            color: socketStatus === 'connected' ? '#198754' : 
                   socketStatus === 'connecting' ? '#ffc107' : '#dc3545',
            fontSize: '0.6rem'
          }}></i>
          {isRefreshing && (
            <span className="ms-1 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          )}
        </Button>
        
        {/* Heartbeat pulse ring */}
        <div className={`heartbeat-ring ${showHeartbeat ? 'animate' : ''}`}></div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xxl"
        centered
        backdrop={true}
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className={`bi bi-circle-fill me-2`} style={{ 
              color: socketStatus === 'connected' ? '#198754' : 
                     socketStatus === 'connecting' ? '#ffc107' : '#dc3545',
              fontSize: '0.6rem'
            }}></i>
            Connection & Provider Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading provider status...
            </div>
          )}
          
          {!loading && connectionStatus && connectionStatus.success && (
            <Row className="g-4">
              {/* Left Column - Connection & Provider Info */}
              <Col xl={4} lg={12} style={{ maxHeight: 'calc(100vh - 12rem)', overflowY: 'auto', padding: '0 1rem' }}>
                <div className="mb-4">
                  <h6 className="border-bottom pb-2">
                    <i className="bi bi-wifi me-2"></i>Real-time Connection
                  </h6>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="d-flex align-items-center mb-2">
                        <Badge bg={getStatusVariant()} className="me-2">
                          {socketStatus === 'connected' 
                            ? 'Connected' 
                            : socketStatus === 'connecting'
                              ? 'Connecting...'
                              : 'Disconnected'}
                        </Badge>
                        {isRefreshing && (
                          <Spinner animation="border" size="sm" />
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <small className="text-muted">
                        {socketStatus === 'connected' 
                          ? 'Real-time price updates active' 
                          : 'Attempting to reconnect...'}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="border-bottom pb-2">
                    <i className="bi bi-cloud-download me-2"></i>Data Provider
                  </h6>
                  <div className="mb-2">
                    <strong>{connectionStatus.provider?.settings?.name || 'Unknown'}</strong>
                    <Badge bg="info" className="ms-2">{connectionStatus.provider?.service}</Badge>
                  </div>
                  
                  {connectionStatus.provider?.settings && (
                    <div className="small">
                      <Row>
                        <Col lg={6}>
                          <div className="mb-2">
                            <i className="bi bi-key-fill me-1"></i>
                            API Key: 
                            <Badge bg={connectionStatus.provider.settings.api_key_configured ? 'success' : 'danger'} className="ms-1">
                              {connectionStatus.provider.settings.api_key_configured ? 'OK' : 'Missing'}
                            </Badge>
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-2">
                            <i className="bi bi-clock me-1"></i>
                            Update Interval: {connectionStatus.provider.settings.min_update_interval}s
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6}>
                          <div className="mb-2">
                            <i className="bi bi-speedometer2 me-1"></i>
                            Rate Limit: {connectionStatus.provider.settings.max_requests_per_minute || connectionStatus.provider.settings.max_requests_per_hour}/minute
                          </div>
                        </Col>
                        {connectionStatus.provider.settings.batch_info && (
                          <Col lg={6}>
                            <div className="mb-2">
                              <i className="bi bi-collection me-1"></i>
                              Batch Size: {connectionStatus.provider.settings.batch_info.fixed_batch_size} symbols (fixed)
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h6 className="border-bottom pb-2">
                    <i className="bi bi-graph-up me-2"></i>Market Status
                  </h6>
                  <div className="d-flex align-items-center">
                    <Badge bg={connectionStatus.market?.is_open ? 'success' : 'secondary'} className="me-2">
                      {connectionStatus.market?.is_open ? 'Market Open' : 'Market Closed'}
                    </Badge>
                    {connectionStatus.market?.info && (
                      <small className="text-muted">{connectionStatus.market.info}</small>
                    )}
                  </div>
                </div>

                {/* Active Tickers Breakdown */}
                {connectionStatus.active_tickers && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2">
                      <i className="bi bi-list-check me-2"></i>Active Tickers ({connectionStatus.active_tickers.total_count})
                    </h6>
                    {connectionStatus.active_tickers.breakdown && (
                      <div className="small">
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="mb-2">
                              <i className="bi bi-graph-up me-1 text-primary"></i>
                              <strong>Status Bar Tickers:</strong> {connectionStatus.active_tickers.breakdown.status_tickers?.count || 0}
                              <small className="text-muted ms-1">(selected)</small>
                            </div>
                            <div className="mb-2">
                              <i className="bi bi-briefcase me-1 text-success"></i>
                              <strong>Active Holdings:</strong> {connectionStatus.active_tickers.breakdown.active_holdings?.count || 0}
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="mb-2">
                              <i className="bi bi-bookmark me-1 text-warning"></i>
                              <strong>Ready List:</strong> {connectionStatus.active_tickers.breakdown.readylist?.count || 0}
                            </div>
                            <div className="mb-2">
                              <i className="bi bi-eye me-1 text-info"></i>
                              <strong>Watch List:</strong> {connectionStatus.active_tickers.breakdown.watchlist?.count || 0}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 text-muted" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Only selected status bar tickers (market indexes or custom tickers) and user tickers are updated. Tickers are counted in their highest priority category to avoid duplicates.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {connectionStatus.active_tickers?.batch_efficiency && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2">
                      <i className="bi bi-speedometer me-2"></i>Batch Efficiency
                    </h6>
                    <div className="small">
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="mb-2">
                            <strong>Batches Needed:</strong> {connectionStatus.active_tickers.batch_efficiency.batches_needed}
                          </div>
                          <div className="mb-2">
                            <strong>Update Time:</strong> ~{connectionStatus.active_tickers.batch_efficiency.estimated_time_minutes} minutes
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="mb-2">
                            <strong>Utilization:</strong> {connectionStatus.active_tickers.batch_efficiency.batch_utilization}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}


              </Col>

              {/* Middle Column - Ticker Details */}
              <Col xl={4} lg={12} className="d-flex flex-column" style={{ maxHeight: 'calc(100vh - 12rem)', padding: '0 1rem' }}>
                {connectionStatus.last_update?.recent_tickers && connectionStatus.last_update.recent_tickers.length > 0 && (
                  <div className="flex-grow-1 d-flex flex-column">
                    <h6 className="border-bottom pb-2 mb-1">
                      <i className="bi bi-list-ul me-2"></i>Recently Updated Tickers ({connectionStatus.last_update.recent_tickers.length})
                    </h6>
                    {recentTickersTimestampInfo && (
                      <div className="text-muted small mb-2">
                        Updated {recentTickersTimestampInfo.relative}
                        {recentTickersTimestampInfo.precise ? ` • ${recentTickersTimestampInfo.precise}` : ''}
                      </div>
                    )}
                    <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
                      <div style={{ 
                        flex: 1,
                        overflowY: 'auto', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '0.375rem',
                        maxHeight: 'calc(70vh - 180px)' 
                      }}>
                        <Table size="sm" className="mb-0">
                          <thead style={{ backgroundColor: 'var(--bs-light)', position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th 
                                style={{ 
                                  padding: '0.5rem 0.75rem', 
                                  borderBottom: '2px solid #dee2e6',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => handleSort('symbol')}
                              >
                                Symbol{getSortIcon('symbol')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '0.5rem 0.75rem', 
                                  borderBottom: '2px solid #dee2e6',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => handleSort('current_price')}
                              >
                                Price{getSortIcon('current_price')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '0.5rem 0.75rem', 
                                  borderBottom: '2px solid #dee2e6',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => handleSort('change')}
                              >
                                Change{getSortIcon('change')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '0.5rem 0.75rem', 
                                  borderBottom: '2px solid #dee2e6',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => handleSort('percent_change')}
                              >
                                %{getSortIcon('percent_change')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSortedTickers(connectionStatus.last_update.recent_tickers).map((ticker, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'table-light' : ''}>
                                <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                                  <strong>{ticker.symbol}</strong>
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', fontFamily: 'monospace', fontWeight: '500' }}>
                                  ${ticker.current_price ? ticker.current_price.toFixed(2) : 'N/A'}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', fontFamily: 'monospace', fontWeight: '500' }}
                                    className={ticker.change > 0 ? 'text-success' : ticker.change < 0 ? 'text-danger' : 'text-muted'}>
                                  {ticker.change ? (ticker.change > 0 ? '+' : '') + ticker.change.toFixed(2) : 'N/A'}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', fontFamily: 'monospace', fontWeight: '500' }}
                                    className={ticker.percent_change > 0 ? 'text-success' : ticker.percent_change < 0 ? 'text-danger' : 'text-muted'}>
                                  {ticker.percent_change ? (ticker.percent_change > 0 ? '+' : '') + ticker.percent_change.toFixed(2) + '%' : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                )}
              </Col>

              {/* Right Column - Distributed System Information */}
              <Col xl={4} lg={12} style={{ maxHeight: 'calc(100vh - 12rem)', overflowY: 'auto', padding: '0 1rem' }}>
                {/* Distributed System Information */}
                {connectionStatus.distributed_system && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2">
                      <i className="bi bi-diagram-3 me-2"></i>Distributed System
                    </h6>
                    <div className="small">
                      {/* Server Coordination */}
                      {connectionStatus.distributed_system.server_coordination && (
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-3">
                            <i className="bi bi-server me-1 text-primary"></i>
                            <strong>Server Coordination</strong>
                          </div>
                          <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong>Current Server:</strong>
                              <Badge bg="info">
                                {connectionStatus.distributed_system.server_coordination.current_server_id || 'Unknown'}
                              </Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong>Lock Status:</strong>
                              <Badge bg={connectionStatus.distributed_system.server_coordination.is_coordinating ? 'success' : 'secondary'}>
                                {connectionStatus.distributed_system.server_coordination.is_coordinating ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong>Lock Holder:</strong>
                              <span className="text-muted">
                                {connectionStatus.distributed_system.server_coordination.lock_holder || 'None'}
                              </span>
                            </div>
                            {connectionStatus.distributed_system.server_coordination.lock_expires && (
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>Expires:</strong>
                                <span className="text-muted">
                                  {new Date(connectionStatus.distributed_system.server_coordination.lock_expires).toLocaleTimeString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Rate Limits */}
                      {connectionStatus.distributed_system.rate_limits && (
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-3">
                            <i className="bi bi-speedometer2 me-1 text-warning"></i>
                            <strong>Rate Limits ({connectionStatus.distributed_system.rate_limits.provider})</strong>
                          </div>
                          {connectionStatus.distributed_system.rate_limits.status && (
                            <div className="mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Per Minute:</strong>
                                <div className="text-end">
                                  <span className={`${connectionStatus.distributed_system.rate_limits.utilization?.per_minute > 80 ? 'text-danger' : connectionStatus.distributed_system.rate_limits.utilization?.per_minute > 60 ? 'text-warning' : 'text-success'}`}>
                                    {connectionStatus.distributed_system.rate_limits.current_usage?.per_minute || 0} / {connectionStatus.distributed_system.rate_limits.limits?.per_minute || 0}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    ({connectionStatus.distributed_system.rate_limits.utilization?.per_minute?.toFixed(1) || 0}%)
                                  </small>
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Per Hour:</strong>
                                <div className="text-end">
                                  <span className={`${connectionStatus.distributed_system.rate_limits.utilization?.per_hour > 80 ? 'text-danger' : connectionStatus.distributed_system.rate_limits.utilization?.per_hour > 60 ? 'text-warning' : 'text-success'}`}>
                                    {connectionStatus.distributed_system.rate_limits.current_usage?.per_hour || 0} / {connectionStatus.distributed_system.rate_limits.limits?.per_hour || 0}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    ({connectionStatus.distributed_system.rate_limits.utilization?.per_hour?.toFixed(1) || 0}%)
                                  </small>
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Per Day:</strong>
                                <div className="text-end">
                                  <span className={`${connectionStatus.distributed_system.rate_limits.utilization?.per_day > 80 ? 'text-danger' : connectionStatus.distributed_system.rate_limits.utilization?.per_day > 60 ? 'text-warning' : 'text-success'}`}>
                                    {connectionStatus.distributed_system.rate_limits.current_usage?.per_day || 0} / {connectionStatus.distributed_system.rate_limits.limits?.per_day || 0}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    ({connectionStatus.distributed_system.rate_limits.utilization?.per_day?.toFixed(1) || 0}%)
                                  </small>
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Capacity:</strong>
                                <span className={capacityPercent !== null ? 'text-info fw-semibold' : 'text-muted'}>
                                  {capacityPercent !== null ? `${capacityPercent}%` : '—'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error handling */}
                      {(connectionStatus.distributed_system.server_coordination?.error || connectionStatus.distributed_system.rate_limits?.error) && (
                        <div className="alert alert-warning py-2 mb-0">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          <small>
                            {connectionStatus.distributed_system.server_coordination?.error || connectionStatus.distributed_system.rate_limits?.error}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Debug Information - moved here for better spacing */}
                {showDebugInfo && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2">
                      <i className="bi bi-bug me-2"></i>Debug Information
                    </h6>
                    <div className={`d-flex align-items-center mb-2 ${lastPriceUpdateTime ? 'text-success' : 'text-warning'}`}>
                      <i className={`bi ${lastPriceUpdateTime ? 'bi-currency-dollar' : 'bi-exclamation-triangle'} me-1`}></i>
                      <span>{getPriceUpdateIndicator()}</span>
                    </div>
                    <small className="text-muted">
                      {lastPriceUpdateTime ? 'Price updates are working' : 'No price updates received yet'}
                    </small>
                  </div>
                )}

                {/* WebSocket Debug Information */}
                <div className="mb-4">
                  <h6 className="border-bottom pb-2">
                    <i className="bi bi-broadcast me-2"></i>WebSocket Status
                  </h6>
                  {(() => {
                    const socketStatus = getSocketStatus();
                    return (
                      <div className="small">
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>Initialized:</strong>
                                <Badge bg={socketStatus.initialized ? 'success' : 'danger'}>
                                  {socketStatus.initialized ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>Connected:</strong>
                                <Badge bg={socketStatus.connected ? 'success' : 'danger'}>
                                  {socketStatus.connected ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>Socket ID:</strong>
                                <span className="text-muted small">
                                  {socketStatus.id || 'None'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>Transport:</strong>
                                <span className="text-muted">
                                  {socketStatus.transport || 'None'}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <strong>URL:</strong>
                                <span className="text-muted small">
                                  {socketStatus.url || 'None'}
                                </span>
                              </div>
                              {socketStatus.error && (
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <strong>Error:</strong>
                                  <span className="text-danger small">
                                    {socketStatus.error}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Debug controls - only show if admin or in development */}
                {(localStorage.getItem('isAdmin') === 'true' || process.env.NODE_ENV === 'development') && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2">
                      <i className="bi bi-tools me-2"></i>Debug Controls
                    </h6>
                    <div className="d-grid gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleTestWebSocket}
                        disabled={isTestingWebSocket}
                        title="Test WebSocket Connection"
                      >
                        {isTestingWebSocket ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-broadcast me-1"></i>
                        )}
                        Test WebSocket
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => {
                          console.log('Forcing complete WebSocket reset...');
                          // First disconnect
                          reconnectSocket();
                          // Wait then reinitialize  
                          setTimeout(() => {
                            console.log('Reinitializing WebSocket...');
                            initSocket();
                          }, 1500);
                        }}
                        title="Force WebSocket Reset"
                      >
                        <i className="bi bi-arrow-repeat me-1"></i>
                        Reset Connection
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={toggleDebugMode}
                        title={showDebugInfo ? "Hide Debug Info" : "Show Debug Info"}
                      >
                        <i className={`bi ${showDebugInfo ? 'bi-bug-fill' : 'bi-bug'} me-1`}></i>
                        {showDebugInfo ? 'Hide Debug' : 'Show Debug'}
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={fetchConnectionStatus}
                        disabled={loading}
                        title="Refresh Provider Status"
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-arrow-clockwise me-1"></i>
                        )}
                        Refresh Status
                      </Button>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          )}

          {!loading && connectionStatus && !connectionStatus.success && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Failed to load provider status: {connectionStatus.error}
            </Alert>
          )}

        </Modal.Body>
      </Modal>
    </>
  );
};

export default StatusButton;
